import axios from 'axios';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption, stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Select, { PxDropdownSc } from 'components/final-form/Select';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import BroadcastEmailRunTable from 'components/guarantors/patient-emails/BroadcastEmailRunTable';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import React, { useEffect, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header } from 'semantic-ui-react';
import { getBroadcastEmailRuns, searchRuns } from 'services/broadcastEmailServices';
import styled from 'styled-components';
import { EmailConsentType } from 'ts-types/api.enums';
import { BroadcastEmailDto, BroadcastEmailRun, BroadcastEmailSearchRequest } from 'ts-types/api.types';

const BroadcastEmailContainer = styled(PraxStyledSegment)`
  height: 100%;

  .side-button {
    margin-left: 1rem;
  }
`;

const StyledGridColumn = styled(Grid.Column)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

const SearchFormDiv = styled.div`

  .actions-row {

    display: flex;
    flex-direction: row;
    align-items: baseline;

    button {
      margin-right: 1rem;
    }

    .submit-message {
      display: flex;
      flex-direction: row;
      align-self: center;

      .ui.inline.loader.active,
      .ui.inline.loader.visible {
        margin: 0 0.9rem;
      }
    }
  }
`;

interface Props extends MessageSourceProps, GuarantorsConsumerRenderProps {

}

const BroadcastEmailRunView = (props: Props) => {

  const {getMessage} = props;

  const [selectedRunId, setSelectedRunId] =
      useState(undefined as number | undefined);

  const [runIdOptions, setRunIdOptions] =
      useState([] as DropdownOption[]);

  // const [displayedRun, setDisplayedRun] = useState(undefined as BroadcastEmailRunDto | undefined);
  const [allRuns, setAllRuns] =
      useState<BroadcastEmailRun[]>([]);

  const [allBroadcastEmails, setAllBroadcastEmails] =
      useState<BroadcastEmailDto[]>([]);

  const emailBroadcastStatusLabels: string[] = Object.values(EmailConsentType);
  const emailBroadcastStatusOptions = stringArrayToDropdownOptionArray(
      emailBroadcastStatusLabels, getMessage, "emailConsent")

  const cancelTokenSource = axios.CancelToken.source();

  const initialValues: Partial<BroadcastEmailSearchRequest> = {
    runId: undefined,
    fromDate: undefined,
    toDate: undefined
  }

  useEffect(() => {
    fetchRuns();
  }, []);

  const fetchRuns = async () => {
    const emailRuns = await getBroadcastEmailRuns(cancelTokenSource);
    const fetchedBroadcastEmails = await searchRuns(initialValues, cancelTokenSource);

    const options: DropdownOption[] = emailRuns.map(run => ({
      key: run.id,
      value: run.id,
      text: run.id
    }));
    setRunIdOptions(options);
    setAllRuns(emailRuns);
    setAllBroadcastEmails(fetchedBroadcastEmails);
  }

  const submitHandler = async (values: Partial<BroadcastEmailSearchRequest>) => {

    const broadcastEmailSearchRequest: Partial<BroadcastEmailSearchRequest> = {
      runId: selectedRunId,
      fromDate: values.fromDate,
      toDate: values.toDate,
      status: values.status
    }

    const fetchedBroadcastEmails = await searchRuns(
        broadcastEmailSearchRequest, cancelTokenSource);

    setAllBroadcastEmails(fetchedBroadcastEmails);
  }

  const renderSearchSegmentForm = (
      {handleSubmit, submitting}: FormRenderProps
  ): React.ReactNode => {

    return (
        <form onSubmit={handleSubmit}>
          <PxGrid columns={1}>
            {/*{errorMessage*/}
            {/*    && (*/}
            {/*        <Grid.Row>*/}
            {/*          <Grid.Column width={16}>*/}
            {/*            <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>*/}
            {/*              {errorMessage}*/}
            {/*            </StyledErrorMessage>*/}
            {/*          </Grid.Column>*/}
            {/*        </Grid.Row>*/}
            {/*    )*/}
            {/*}*/}
            <Grid.Row>
              <Grid.Column>
                <Header>{getMessage("patientEmail.emailBatches")}</Header>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <StyledGridColumn width={7}>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      <Header size="tiny">{getMessage("testAuthorityNotification.fromDate")}</Header>
                    </Grid.Column>
                    <Grid.Column width={5} verticalAlign="middle">
                      <Header size="tiny">{getMessage("testAuthorityNotification.toDate")}</Header>
                    </Grid.Column>
                    <Grid.Column width={6} verticalAlign="middle">
                      <Header size="tiny">{getMessage("patient.status")}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      <div className="actions-row">
                        <Field
                            name="fromDate"
                            clearable
                            emptyDateInput
                            component={DatePicker}
                            fluid
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <div className="actions-row">
                        <Field
                            name="toDate"
                            clearable
                            emptyDateInput
                            component={DatePicker}
                            fluid
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column width={6}>
                      <div className="actions-row">
                        <Field
                            name="status"
                            clearable
                            component={Select}
                            options={emailBroadcastStatusOptions}
                        />
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                </PxGrid>
              </StyledGridColumn>

              <StyledGridColumn width={7}>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column width={10} verticalAlign="middle">
                      <Header size="tiny">{getMessage("patientEmail.runs.title")}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={10}>
                      <div className="actions-row">
                        <PxDropdownSc
                            name="runId"
                            selection
                            clearable
                            search
                            value={selectedRunId}
                            onChange={(e: any, {value}: any) => {
                              setSelectedRunId(value)
                            }}
                            options={runIdOptions}
                        />

                        <Button
                            className="side-button"
                            type="submit"
                            primary
                            compact
                            size="small"
                            disabled={submitting}
                        >
                          {getMessage("search")}
                        </Button>
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                </PxGrid>
              </StyledGridColumn>
            </Grid.Row>
          </PxGrid>
        </form>
    )
  }

  const renderSearchSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={submitHandler}
            initialValues={initialValues}
            subscription={{pristine: true, submitting: true, values: true}}
            render={(formProps: FormRenderProps) => renderSearchSegmentForm(formProps)}
        />
    );
  };

  return (
      <BroadcastEmailContainer>
        <SearchFormDiv>
          {renderSearchSegment()}
        </SearchFormDiv>

        <StyledViewTableRow id="patientEmailImportTable">
          <BroadcastEmailRunTable emails={allBroadcastEmails}/>
        </StyledViewTableRow>

        {/*<FormButtonsRow>*/}
        {/*  <Button*/}
        {/*      primary*/}
        {/*      compact*/}
        {/*      type="button"*/}
        {/*      onClick={async () => {*/}
        {/*        if (displayedRun) {*/}
        {/*          const updatedRun = await updateBroadcastEmailRun(displayedRun.id, cancelTokenSource);*/}
        {/*          setDisplayedRun(updatedRun);*/}
        {/*        }*/}
        {/*      }}*/}
        {/*      disabled={displayedRun === undefined}*/}
        {/*  >*/}
        {/*    {getMessage("patientEmail.updateBroadcastEmailRun.button")}*/}
        {/*  </Button>*/}
        {/*</FormButtonsRow>*/}

      </BroadcastEmailContainer>
  );
}

export default withMessages(withGuarantorsContext(BroadcastEmailRunView));
